import { SignalAbortedError } from '../util/internals/console';
import { createCanvasElement, createImage } from '../util/misc/dom';

export type FilterImageObject = {
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  cacheKey: string;
  getElement(): HTMLImageElement | HTMLCanvasElement;
  toObject(): Record<string, unknown>;
};

type SerializedCanvasImage = {
  left?: unknown;
  top?: unknown;
  scaleX?: unknown;
  scaleY?: unknown;
  cacheKey?: unknown;
  src?: unknown;
  width?: unknown;
  height?: unknown;
} & Record<string, unknown>;

const isNodeRuntime = (): boolean =>
  typeof (globalThis as { process?: { versions?: { node?: string } } }).process
    ?.versions?.node === 'string';

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const asPositiveInt = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  const rounded = Math.round(value);
  return rounded > 0 ? rounded : fallback;
};

const resolveCacheKey = (
  serialized: SerializedCanvasImage,
  src: string | undefined,
  width: number,
  height: number,
): string =>
  asNonEmptyString(serialized.cacheKey) ??
  `canvas-image:${src ?? 'inline'}:${width}x${height}`;

const createPlaceholderCanvas = (
  serialized: SerializedCanvasImage,
): HTMLCanvasElement => {
  const element = createCanvasElement();
  element.width = asPositiveInt(serialized.width, 1);
  element.height = asPositiveInt(serialized.height, 1);
  return element;
};

const loadImageElement = (
  image: HTMLImageElement,
  src: string,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (isNodeRuntime()) {
      image.src = src;
      resolve();
      return;
    }

    const cleanup = (): void => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = (): void => {
      cleanup();
      reject(new SignalAbortedError('CanvasImage.fromObject'));
    };

    image.onload = () => {
      cleanup();
      resolve();
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`CanvasImage.fromObject failed to load image: ${src}`));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    image.src = src;

    if (image.complete) {
      cleanup();
      resolve();
    }
  });

export class CanvasImage implements FilterImageObject {
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  cacheKey: string;

  private readonly element: HTMLImageElement | HTMLCanvasElement;
  private readonly serialized: SerializedCanvasImage;

  constructor(
    element: HTMLImageElement | HTMLCanvasElement,
    serialized: SerializedCanvasImage,
  ) {
    this.element = element;
    this.serialized = serialized;
    this.left = asNumber(serialized.left, 0);
    this.top = asNumber(serialized.top, 0);
    this.scaleX = asNumber(serialized.scaleX, 1);
    this.scaleY = asNumber(serialized.scaleY, 1);
    this.cacheKey = resolveCacheKey(
      serialized,
      asNonEmptyString(serialized.src),
      element.width || 1,
      element.height || 1,
    );
  }

  getElement(): HTMLImageElement | HTMLCanvasElement {
    return this.element;
  }

  toObject(): Record<string, unknown> {
    return {
      ...this.serialized,
      left: this.left,
      top: this.top,
      scaleX: this.scaleX,
      scaleY: this.scaleY,
      cacheKey: this.cacheKey,
    };
  }

  static async fromObject(
    object: Record<string, unknown>,
    options: { signal?: AbortSignal } = {},
  ): Promise<CanvasImage> {
    if (options.signal?.aborted) {
      throw new SignalAbortedError('CanvasImage.fromObject');
    }

    const serialized = object as SerializedCanvasImage;
    const src = asNonEmptyString(serialized.src);

    if (!src) {
      return new CanvasImage(createPlaceholderCanvas(serialized), serialized);
    }

    const image = createImage();
    await loadImageElement(image, src, options.signal);
    return new CanvasImage(image, serialized);
  }
}
